import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react";
import { AuthShell } from "@/components/auth/auth-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMe, useWorkspaceLogin } from "@/hooks/use-auth";
import { authErrorMessage } from "@/lib/auth-errors";

function defaultTarget(role?: string | null): string {
  return role === "ADMIN" ? "/admin" : "/app";
}

export default function LoginPage() {
  const navigate = useNavigate();
  const loc = useLocation();
  const login = useWorkspaceLogin();
  const { data: me, isLoading: meLoading } = useMe();

  const next = new URLSearchParams(loc.search).get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Already signed in? Bounce.
  if (!meLoading && me) {
    return <Navigate to={next ?? defaultTarget(me.role)} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email || !password || login.isPending) return;
    try {
      const res = await login.mutateAsync({ email, password });
      const dest = next ?? defaultTarget(res.role);
      if (res.mustChangePassword) {
        navigate(`/change-password?next=${encodeURIComponent(dest)}`, { replace: true });
      } else {
        navigate(dest, { replace: true });
      }
    } catch {
      /* surfaced via login.error */
    }
  }

  const err = authErrorMessage(login.error);
  const canSubmit = email.length > 0 && password.length > 0 && !login.isPending;

  return (
    <AuthShell>
      <div className="animate-fade-in-up rounded-2xl border border-border bg-card/70 p-6 shadow-card backdrop-blur md:p-8">
        <h1 className="font-display text-3xl font-black tracking-tight">
          Welcome back
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Sign in to your imgwala Lab workspace.
        </p>

        <form onSubmit={onSubmit} className="mt-7 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={login.isPending}
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link
                to="/forgot-password"
                className="text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                Forgot?
              </Link>
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={login.isPending}
                className="pr-9"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 grid w-9 place-items-center text-muted-foreground transition-colors hover:text-foreground"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {err && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            className="group w-full"
            disabled={!canSubmit}
          >
            {login.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Signing in…
              </>
            ) : (
              <>
                Sign in
                <ArrowRight className="transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </Button>
        </form>
      </div>

      <div className="mt-6 space-y-2 text-center text-sm text-muted-foreground">
        <div>
          New here?{" "}
          <Link
            to="/register"
            className="font-medium text-foreground transition-colors hover:text-brand"
          >
            Request access
          </Link>
        </div>
        <div className="text-xs">
          Platform administrator?{" "}
          <Link
            to="/superadmin/login"
            className="font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            Superadmin sign in
          </Link>
        </div>
      </div>
    </AuthShell>
  );
}
